


import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Api } from "../Api/Api";

const Admin = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null); // Tracks if we are editing
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    role: "staff",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "null");
  const isAuthorized = currentUser && currentUser.role === "admin";

  useEffect(() => {
    if (!isAuthorized) {
      navigate("/");
      return;
    }
    loadUsers();
  }, []);

  const loadUsers = async () => {
    const res = await fetch(`${Api}api/get-users.php`);
    const data = await res.json();
    setUsers(data.users || []);
  };

  const handleEdit = (user) => {
    setEditingUserId(user.userId);
    setFormData({
      username: user.username,
      password: "", // Leave empty to keep old password or type new one
      role: user.role,
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Use update API if editing, otherwise use create API
    const endpoint = editingUserId ? "update-user.php" : "create-user.php";
    
    try {
      const res = await fetch(`${Api}api/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          userId: editingUserId, 
          createdBy: currentUser.userId,
        }),
      });

      const data = await res.json();

      if (!data.status) {
        setError(data.message);
        return;
      }

      setSuccess(editingUserId ? "User updated successfully" : "User created successfully");
      resetForm();
      loadUsers();
      setTimeout(() => setShowForm(false), 1200);
    } catch (err) {
      setError("Server error");
    }
  };

  const resetForm = () => {
    setFormData({ username: "", password: "", role: "staff" });
    setEditingUserId(null);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure to delete this user?")) return;
    const res = await fetch(`${Api}api/delete-user.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId }),
    });
    const data = await res.json();
    if (data.status) loadUsers();
  };

  const inputClass = "w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm";
  const labelClass = "block text-xs font-bold text-gray-700 uppercase mb-1";

  return (
    <div
      className="min-h-screen bg-gray-50 p-8"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex justify-between items-center border-b pb-6">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900">
              SAL Control Panel
            </h1>
            <p className="text-gray-500 text-sm">
              Manage system access and roles.
            </p>
          </div>
          {!showForm && (
            <button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-lg font-bold shadow-sm"
            >
              + Add New User
            </button>
          )}
        </div>

        {/* Form Section */}
        {showForm && (
          <div className="bg-white border border-gray-200 rounded-xl shadow-xl p-8 max-w-lg mx-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-6">
              {editingUserId ? "Edit User Account" : "Create Access Account"}
            </h2>
            {error && (
              <div className="bg-red-50 text-red-700 p-3 mb-4 rounded text-xs font-bold">
                {error}
              </div>
            )}
            {success && (
              <div className="bg-green-50 text-green-700 p-3 mb-4 rounded text-xs font-bold">
                {success}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className={labelClass}>Username</label>
                <input
                  type="text"
                  className={inputClass}
                  value={formData.username}
                  onChange={(e) =>
                    setFormData({ ...formData, username: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <label className={labelClass}>
                  {editingUserId
                    ? "New Password (Leave blank to keep same)"
                    : "Password"}
                </label>
                <input
                  type="password"
                  className={inputClass}
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  required={!editingUserId}
                />
              </div>
              <div>
                <label className={labelClass}>System Role</label>
                <select
                  className={inputClass}
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value })
                  }
                >
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-indigo-600 text-white py-2.5 rounded-lg font-bold hover:bg-indigo-700"
                >
                  {editingUserId ? "Update User" : "Verify & Create"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 border border-gray-300 text-gray-600 py-2.5 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 text-xs font-bold text-gray-500 uppercase">
              <tr>
                <th className="px-6 py-4">S.No</th>
                <th className="px-6 py-4">Username</th>
                <th className="px-6 py-4">Hashed Password</th>
                <th className="px-6 py-4">Role</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((u, index) => (
                <tr
                  key={u.userId}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4 text-gray-400 font-medium">
                    {index + 1}
                  </td>
                  <td className="px-6 py-4 font-bold text-gray-900">
                    {u.username}
                  </td>
                  {/* <td className="px-6 py-4">
                    <code className="text-[10px] bg-gray-100 p-1 rounded text-gray-500">
                      {u.password.substring(0, 20)}...
                    </code>
                  </td> */}
                  <td className="px-6 py-4">
                    <code className="text-[10px] bg-gray-100 p-1 rounded text-gray-500">
                      {u.password ? u.password.substring(0, 20) + "..." : "N/A"}
                    </code>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase ${u.role === "admin" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}
                    >
                      {u.role}
                    </span>
                  </td>
                   <td className="px-6 py-4 text-right space-x-2">
                     <button
                       onClick={() => handleEdit(u)}
                       className="text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-md font-bold text-xs hover:bg-indigo-100"
                     >
                       Edit
                     </button>
                     <button
                       onClick={() => handleDeleteUser(u.userId)}
                       className="text-red-500 bg-red-50 px-3 py-1.5 rounded-md font-bold text-xs hover:bg-red-100"
                     >
                       Revoke
                     </button>
                   </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Admin;